import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  api,
  isExtensionAvailable,
  pingExtension,
  registerClientViaExtension,
} from '../api/client';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Checkbox,
} from '@databro/ui';

const EXTENSION_ID = import.meta.env.VITE_EXTENSION_ID || '';

export default function Clients() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [copiedKey, setCopiedKey] = useState(false);

  const [extensionStatus, setExtensionStatus] = useState<
    'unknown' | 'checking' | 'connected' | 'not_found' | 'not_configured'
  >('unknown');
  const [autoRegisterEnabled, setAutoRegisterEnabled] = useState(false);
  const [registeringViaExtension, setRegisteringViaExtension] = useState(false);
  const [extensionError, setExtensionError] = useState('');

  useEffect(() => {
    if (!EXTENSION_ID) {
      setExtensionStatus('not_configured');
      return;
    }
    checkExtensionConnection();
  }, []);

  const checkExtensionConnection = async () => {
    if (!EXTENSION_ID || !isExtensionAvailable()) {
      setExtensionStatus('not_found');
      return;
    }

    setExtensionStatus('checking');
    const response = await pingExtension(EXTENSION_ID);

    if (response.success) {
      setExtensionStatus('connected');
      setAutoRegisterEnabled(true);
    } else {
      setExtensionStatus('not_found');
      setAutoRegisterEnabled(false);
    }
  };

  const { data: clients, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.getClients(),
  });

  const createMutation = useMutation({
    mutationFn: (data: { id: string; name?: string }) => api.createClient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setShowCreateForm(false);
      setNewClientName('');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.updateClient(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setEditingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const handleCreateClient = async () => {
    setExtensionError('');

    if (autoRegisterEnabled && extensionStatus === 'connected' && EXTENSION_ID) {
      // Register via extension - extension will generate/use its persistent clientId
      setRegisteringViaExtension(true);

      try {
        const apiUrl = window.location.origin;

        // The dashboard authenticates via session cookie, so there's no static
        // key to hand off. Mint a fresh API key scoped to this extension
        // instance (visible/deletable in the API Keys UI) and pass it along.
        const keyName = `Extension: ${newClientName || 'Client'}`.slice(0, 50);
        const created = await api.createApiKey(keyName);
        const apiKey = created.key;

        const response = await registerClientViaExtension(EXTENSION_ID, {
          apiUrl,
          apiKey,
          clientName: newClientName || undefined,
        });

        if (response.success) {
          // Extension registered successfully - it used its own persistent clientId
          queryClient.invalidateQueries({ queryKey: ['clients'] });
          setShowCreateForm(false);
          setNewClientName('');
        } else {
          setExtensionError(response.error || 'Failed to register via extension');
        }
      } catch (error) {
        setExtensionError(
          error instanceof Error ? error.message : 'Extension registration failed'
        );
      } finally {
        setRegisteringViaExtension(false);
      }
    } else {
      // Manual registration - generate a clientId (user will need to manually configure extension)
      const clientId = crypto.randomUUID();
      createMutation.mutate({ id: clientId, name: newClientName || undefined });
    }
  };

  const handleCopyApiKey = () => {
    const storedKey = api.getApiKey();
    if (storedKey) {
      navigator.clipboard.writeText(storedKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  const currentApiKey = api.getApiKey();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">Clients</h1>
          <p className="text-muted-foreground">
            Connected Chrome extension instances (one per Chrome profile)
          </p>
        </div>
        <Button onClick={() => setShowCreateForm(true)}>+ Add Client</Button>
      </div>

      {currentApiKey && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">API Key</CardTitle>
            <CardDescription>
              Use this key to connect the Chrome extension
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <code className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                {currentApiKey.slice(0, 12)}...{currentApiKey.slice(-4)}
              </code>
              <Button variant="secondary" size="sm" onClick={handleCopyApiKey}>
                {copiedKey ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
            <DialogDescription>
              Create a new client to connect a Chrome extension instance.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clientName">Client Name (optional)</Label>
              <Input
                id="clientName"
                value={newClientName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewClientName(e.target.value)}
                placeholder="e.g., Shopee Store 1"
              />
            </div>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">
                    Auto-Register via Extension
                  </CardTitle>
                  <Badge
                    variant={
                      extensionStatus === 'connected'
                        ? 'success'
                        : extensionStatus === 'checking'
                        ? 'warning'
                        : 'secondary'
                    }
                  >
                    {extensionStatus === 'connected'
                      ? 'Connected'
                      : extensionStatus === 'checking'
                      ? 'Checking...'
                      : extensionStatus === 'not_found'
                      ? 'Not Found'
                      : extensionStatus === 'not_configured'
                      ? 'Not Configured'
                      : 'Unknown'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {extensionStatus === 'connected' && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="autoRegister"
                      checked={autoRegisterEnabled}
                      onCheckedChange={(checked: boolean | 'indeterminate') =>
                        setAutoRegisterEnabled(checked === true)
                      }
                    />
                    <Label htmlFor="autoRegister" className="text-sm font-normal">
                      Automatically configure extension with this client
                    </Label>
                  </div>
                )}

                {extensionStatus === 'not_configured' && (
                  <p className="text-sm text-muted-foreground">
                    Set <code className="bg-muted px-1 rounded">VITE_EXTENSION_ID</code> in{' '}
                    <code className="bg-muted px-1 rounded">packages/web/.env</code> and restart.
                  </p>
                )}

                {extensionStatus === 'not_found' && (
                  <p className="text-sm text-muted-foreground">
                    Extension not detected. Make sure the DataBro extension is installed.
                  </p>
                )}
              </CardContent>
            </Card>

            {extensionError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                {extensionError}
              </div>
            )}

            {autoRegisterEnabled && extensionStatus === 'connected' && (
              <div className="p-3 bg-green-500/10 text-green-600 rounded-md text-sm">
                The extension will be automatically configured when you create this client.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateForm(false);
                setNewClientName('');
                setExtensionError('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateClient}
              disabled={createMutation.isPending || registeringViaExtension}
            >
              {registeringViaExtension
                ? 'Registering...'
                : createMutation.isPending
                ? 'Creating...'
                : autoRegisterEnabled && extensionStatus === 'connected'
                ? 'Create & Configure'
                : 'Create Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name / ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
              <TableHead>Last Seen</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients?.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No clients connected yet. Install the Chrome extension to get started.
                </TableCell>
              </TableRow>
            )}
            {clients?.map((client) => (
              <TableRow key={client.id}>
                <TableCell>
                  {editingId === client.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditName(e.target.value)}
                        placeholder="Client name"
                        className="h-8 w-40"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        onClick={() =>
                          updateMutation.mutate({ id: client.id, name: editName })
                        }
                      >
                        Save
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Link
                        to={`/clients/${client.id}`}
                        className="font-medium hover:underline"
                      >
                        {client.name || 'Unnamed Client'}
                      </Link>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                          {client.id}
                        </code>
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-xs"
                          onClick={() => navigator.clipboard.writeText(client.id)}
                        >
                          Copy
                        </Button>
                      </div>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        client.isOnline ? 'bg-green-500' : 'bg-muted-foreground/30'
                      }`}
                    />
                    <span className="text-sm">
                      {client.isOnline ? 'Online' : 'Offline'}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {client.actionCount} bound
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {client.lastSeenAt
                    ? new Date(client.lastSeenAt).toLocaleString()
                    : 'Never'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingId(client.id);
                        setEditName(client.name || '');
                      }}
                    >
                      Rename
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => {
                        if (
                          confirm(
                            'Are you sure you want to delete this client? This will unbind all associated actions.'
                          )
                        ) {
                          deleteMutation.mutate(client.id);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="bg-primary/5 border-primary/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">How to connect a client</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
            <li>Click "+ Add Client" above to create a new client</li>
            <li>Install the DataBro Chrome extension</li>
            <li>Open the extension and enter the API URL, API Key, and Client ID</li>
            <li>The client will show as "Online" once connected</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
